import { Link } from "@tanstack/react-router";
import { LayoutTemplate, Loader2, Star } from "lucide-react";
import type { JSX } from "react";
import { useId } from "react";
import { useTranslation } from "react-i18next";
import { AppConfig } from "@/common/appConfig";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { underV3, V3_ICON_TILE, V3_LINK } from "@/components/ui/v3/flagged";
import { useTemplatePreferences } from "@/hooks/templates/useTemplatePreferences";
import { toast } from "@/hooks/use-toast";
import { splitFavorites, templateChoices } from "@/lib/template-preferences";
import { cn } from "@/lib/utils";
import { SETTINGS_CARD } from "../redesign/SettingsCard";

// Settings > Note defaults: the template every new visit starts with. Same
// rule and writes as "Set as default" on the Templates page.
export const DefaultTemplateSetting = (): JSX.Element => {
  const { t } = useTranslation();
  const id = useId();
  const {
    templates,
    loading,
    error,
    defaultTemplate,
    favorites,
    setDefault,
    saving,
  } = useTemplatePreferences();
  const choices = templateChoices(templates);
  const groups = splitFavorites(choices, favorites);
  const builtIn = groups.others.filter((choice) => choice.builtIn);
  const yours = groups.others.filter((choice) => !choice.builtIn);
  const nameOf = (value: string): string =>
    choices.find((choice) => choice.value === value)?.name ?? value;

  return (
    <section
      aria-labelledby={`${id}-title`}
      className={
        AppConfig.enableAppRedesign
          ? SETTINGS_CARD
          : "rounded-[15px] border bg-background p-4 sm:p-6"
      }
      data-testid="settings-default-template"
    >
      <div className="flex items-start gap-3 sm:gap-4">
        <div
          className={cn(
            "flex size-10 shrink-0 items-center justify-center rounded-full bg-v3-brand-tint text-v3-brand sm:size-11",
            underV3(V3_ICON_TILE),
            underV3("size-9 text-v3-text-2 sm:size-10 [&_svg]:size-[18px]"),
          )}
        >
          <LayoutTemplate aria-hidden className="size-5" />
        </div>
        <div className="min-w-0 flex-1 space-y-1">
          <h3
            className={cn(
              "text-base font-bold leading-snug sm:text-lg",
              underV3(
                "font-v3-display text-[16px] font-semibold sm:text-[16px]",
              ),
            )}
            id={`${id}-title`}
          >
            {t("settings.transcription.defaultTemplate.label")}
          </h3>
          <p
            className={cn(
              "text-sm text-muted-foreground",
              underV3("text-[14px] leading-relaxed text-v3-text-2"),
            )}
          >
            {t("settings.transcription.defaultTemplate.hint")}
          </p>
        </div>
      </div>
      {error && (
        <p
          className={cn(underV3("mt-3 text-[14px] text-v3-rec-text sm:ml-14"))}
          role="alert"
        >
          {t("error.unknown")}
        </p>
      )}
      <div
        className={cn(
          "mt-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3",
          underV3("sm:ml-14"),
        )}
      >
        <Select
          disabled={loading || !!saving}
          value={defaultTemplate}
          onValueChange={(value) => {
            if (value === defaultTemplate) return;
            void setDefault(value).then((saved) => {
              toast(
                saved
                  ? {
                      variant: "success",
                      title: t("templates.favorites.defaultSaved", {
                        name: nameOf(value),
                      }),
                    }
                  : {
                      variant: "error",
                      title: t("templates.favorites.defaultFailed"),
                    },
              );
            });
          }}
        >
          <SelectTrigger
            aria-labelledby={`${id}-title`}
            className="min-h-11 w-full sm:min-h-10 sm:max-w-sm"
            data-testid="settings-default-template-select"
          >
            {saving ? (
              <span className="flex items-center gap-2">
                <Loader2 aria-hidden className="size-4 animate-spin" />
                {nameOf(saving)}
              </span>
            ) : (
              <SelectValue />
            )}
          </SelectTrigger>
          <SelectContent>
            {groups.favorites.length > 0 && (
              <SelectGroup>
                <SelectLabel className="flex items-center gap-1.5">
                  <Star
                    aria-hidden
                    className="size-3.5 fill-v3-warn text-v3-warn"
                  />
                  {t("templates.favorites.group")}
                </SelectLabel>
                {groups.favorites.map((choice) => (
                  <SelectItem key={choice.value} value={choice.value}>
                    {choice.name}
                  </SelectItem>
                ))}
              </SelectGroup>
            )}
            {builtIn.length > 0 && (
              <SelectGroup>
                <SelectLabel>
                  {t("templates.favorites.builtInGroup")}
                </SelectLabel>
                {builtIn.map((choice) => (
                  <SelectItem key={choice.value} value={choice.value}>
                    {choice.name}
                  </SelectItem>
                ))}
              </SelectGroup>
            )}
            {yours.length > 0 && (
              <SelectGroup>
                <SelectLabel>{t("templates.favorites.yoursGroup")}</SelectLabel>
                {yours.map((choice) => (
                  <SelectItem key={choice.value} value={choice.value}>
                    {choice.name}
                  </SelectItem>
                ))}
              </SelectGroup>
            )}
          </SelectContent>
        </Select>
        <Link
          className={cn(
            "inline-flex min-h-11 items-center text-sm font-medium text-v3-brand hover:underline sm:min-h-10",
            underV3(V3_LINK),
          )}
          to="/templates"
        >
          {t("settings.transcription.defaultTemplate.manage")}
        </Link>
      </div>
    </section>
  );
};
